import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Gift, Mail, CheckCircle2, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAccess } from "../context/AccessContext";
import { useToast } from "../hooks/use-toast";
import { subscribeNewsletter as apiSubscribe, ebookUrl } from "../api";
import { PROGRAMS } from "../mock";
import Seo from "../components/Seo";

const Newsletter = () => {
  const { access, subscribeNewsletter } = useAccess();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [program, setProgram] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [access.newsletter]);

  const submit = async (e) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await apiSubscribe(email, name, program, consent);
      subscribeNewsletter(email);
      toast({ title: "You're in! \ud83c\udf89", description: "Your free eBook is on its way to your inbox — and you can download it right here too." });
    } catch (err) {
      console.error("Newsletter save failed", err);
      setError("Something went wrong — please try again or call 341-688-6866.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24">
      <Seo
        title="Free BJJ eBook & Newsletter | Alameda Brazilian Jiu-Jitsu"
        description="Join the Alameda BJJ newsletter and get a free Beginner's Guide to Brazilian Jiu-Jitsu, Self-Defense & Verbal Jiu-Jitsu — plus tips, specials and event invites."
        path="/newsletter"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Newsletter", path: "/newsletter" }]}
      />
      <section className="relative py-24 overflow-hidden bg-[#0E1613]">
        <div className="absolute top-0 right-0 h-72 w-72 bg-br-green/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 h-64 w-64 bg-br-yellow/10 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-br-yellow tracking-wide">
              <Gift size={14} /> Free Gift Inside
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl font-extrabold text-white leading-tight">
              Join Our Newsletter & Get a FREE eBook
            </h1>
            <p className="mt-5 text-neutral-300 leading-relaxed">
              Sign up and instantly unlock our schedule plus a free eBook on Brazilian Jiu-Jitsu, Self-Defense & Verbal Jiu-Jitsu™ — everything parents and beginners should know.
            </p>
            <ul className="mt-7 space-y-3">
              {["FREE BJJ & Self-Defense eBook", "Instant access to the class schedule", "Specials, tips & event invites"].map((p, i) => (
                <li key={i} className="flex items-center gap-3 text-neutral-200">
                  <CheckCircle2 size={19} className="text-br-green" /> {p}
                </li>
              ))}
            </ul>
          </div>

          <div>
            {!access.newsletter ? (
              <div className="reveal bg-white rounded-2xl p-8 sm:p-10 shadow-2xl">
                <div className="h-14 w-14 rounded-full bg-br-yellow/20 flex items-center justify-center mb-5">
                  <BookOpen className="text-br-blue" size={26} />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#1A1A1A]">Get Your FREE BJJ & Self-Defense eBook</h3>
                <p className="text-neutral-600 mt-2">Tell us a little about you and we'll email the PDF right away.</p>
                <form onSubmit={submit} className="mt-6 space-y-3" data-testid="ebook-signup-form">
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="ebook-name-input" className="h-12 rounded-full border-neutral-300 px-5" />
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="ebook-email-input" className="h-12 rounded-full border-neutral-300 pl-11" />
                  </div>
                  <Select value={program} onValueChange={setProgram}>
                    <SelectTrigger className="h-12 rounded-full border-neutral-300 px-5" data-testid="ebook-program-select">
                      <SelectValue placeholder="I'm interested in..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PROGRAMS.map((p) => (
                        <SelectItem key={p.id} value={p.title}>{p.title} — {p.ages}</SelectItem>
                      ))}
                      <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="flex items-start gap-3 text-xs text-neutral-600 leading-snug cursor-pointer pt-1">
                    <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} data-testid="ebook-consent-checkbox" className="mt-0.5" />
                    <span>Yes, send me tips, free-trial invites and membership specials from Alameda BJJ by email. Optional — unsubscribe anytime.</span>
                  </label>
                  {error && <p className="text-sm text-red-500" data-testid="ebook-error">{error}</p>}
                  <Button type="submit" disabled={loading} data-testid="ebook-submit-btn" className="w-full h-12 bg-br-green hover:bg-[#00822f] text-white rounded-full text-base font-semibold disabled:opacity-70">
                    {loading ? "Sending..." : "Send Me the Free eBook"}
                  </Button>
                </form>
                <p className="text-xs text-neutral-400 mt-4 text-center">We only email what you ask for. Unsubscribe anytime.</p>
              </div>
            ) : (
              <div className="reveal bg-white rounded-2xl p-8 sm:p-10 shadow-2xl text-center" data-testid="ebook-success">
                <div className="mx-auto h-16 w-16 rounded-full bg-br-green/10 flex items-center justify-center mb-5">
                  <CheckCircle2 className="text-br-green" size={32} />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#1A1A1A]">Check Your Inbox!</h3>
                <p className="text-neutral-600 mt-3">Thanks for joining, {access.email}. We've emailed your free eBook — you can also download it right now below.</p>
                <div className="mt-7 space-y-3">
                  <a href={ebookUrl} target="_blank" rel="noreferrer">
                    <Button className="w-full h-12 bg-br-green hover:bg-[#00822f] text-white rounded-full font-semibold">
                      <BookOpen size={18} className="mr-2" /> Download Free eBook
                    </Button>
                  </a>
                  <Link to="/schedule">
                    <Button variant="outline" className="w-full h-12 rounded-full border-neutral-300 font-semibold">
                      <Calendar size={18} className="mr-2" /> View Class Schedule
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-neutral-400 mt-4">Your eBook is a downloadable PDF guide.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Newsletter;
